import { Feedback } from "@/types/feedback";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface FeedbackDetailProps {
  feedback: Feedback | null;
  onClose: () => void;
  onStatusChange: (id: string, status: string) => void;
}

const statusLabels: Record<string, string> = {
  new: "Novo",
  in_progress: "Em Andamento",
  resolved: "Resolvido",
  ignored: "Ignorado",
};

const typeLabels: Record<string, string> = {
  bug: "Erro",
  suggestion: "Sugestão",
};

export function FeedbackDetail({
  feedback,
  onClose,
  onStatusChange,
}: FeedbackDetailProps) {
  if (!feedback) return null;

  return (
    <Dialog open={!!feedback} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Detalhes do Feedback</DialogTitle>
          <DialogDescription>
            Enviado em {format(new Date(feedback.created_at), "PPpp", { locale: ptBR })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold mb-1">Tipo</h4>
              <Badge variant={feedback.type === "bug" ? "destructive" : "default"}>
                {typeLabels[feedback.type] || feedback.type}
              </Badge>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Status</h4>
               <Select
                  value={feedback.status}
                  onValueChange={(value) => onStatusChange(feedback.id, value)}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">Novo</SelectItem>
                    <SelectItem value="in_progress">Em Andamento</SelectItem>
                    <SelectItem value="resolved">Resolvido</SelectItem>
                    <SelectItem value="ignored">Ignorado</SelectItem>
                  </SelectContent>
                </Select>
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-1">Mensagem</h4>
            <div className="p-3 bg-muted rounded-md text-sm whitespace-pre-wrap">
              {feedback.message}
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-1">Contexto</h4>
            <div className="grid grid-cols-1 gap-2 text-sm">
                <div><strong>URL:</strong> {feedback.context.url}</div>
                <div><strong>User Agent:</strong> {feedback.context.userAgent}</div>
            </div>
          </div>

          {feedback.logs && feedback.logs.length > 0 && (
            <div>
              <h4 className="font-semibold mb-1">Logs do Sistema</h4>
              <ScrollArea className="h-[200px] w-full rounded-md border p-4 bg-card text-foreground font-mono text-xs">
                {feedback.logs.map((log, index) => (
                  <div key={index} className="mb-1">
                    <span className="text-muted-foreground">
                      {format(new Date(log.timestamp), "HH:mm:ss.SSS")}
                    </span>{" "}
                    <span
                      className={
                        log.level === "error"
                          ? "text-red-500"
                          : log.level === "warn"
                          ? "text-yellow-500"
                          : "text-blue-400"
                      }
                    >
                      [{log.level.toUpperCase()}]
                    </span>{" "}
                    {log.message}
                    {log.data && log.data.length > 0 && (
                         <span className="text-muted-foreground"> {JSON.stringify(log.data)}</span>
                    )}
                  </div>
                ))}
              </ScrollArea>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
